import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { profileService } from '../services/profileService';
import { Button, Card, Input, Label, ErrorMessage } from '../styles/GlobalStyles';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  padding: var(--spacing-lg);
`;

const PasswordCard = styled(Card)`
  max-width: 500px;
  width: 100%;
`;

const Title = styled.h1`
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const SuccessMessage = styled.div`
  background: var(--success-color);
  color: white;
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const PasswordRequirements = styled.ul`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: var(--spacing-sm);
  padding-left: var(--spacing-lg);
  line-height: 1.6;
`;

export const CompletePasswordChangePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid password reset link. No token provided.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid token');
      return;
    }

    setLoading(true);

    try {
      await profileService.completePasswordChange(token, newPassword, confirmPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!token && !error) {
    return null;
  }

  return (
    <Container>
      <PasswordCard>
        <Title>Set New Password</Title>

        {success ? (
          <>
            <SuccessMessage>
              Password updated successfully! Redirecting to login...
            </SuccessMessage>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={loading || !token}
                minLength={8}
              />
              <PasswordRequirements>
                <li>At least 8 characters long</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include at least one number</li>
              </PasswordRequirements>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={loading || !token}
                minLength={8}
              />
            </div>

            <Button type="submit" disabled={loading || !token}>
              {loading ? 'Updating Password...' : 'Update Password'}
            </Button>

            <Button
              type="button"
              $variant="secondary"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Form>
        )}
      </PasswordCard>
    </Container>
  );
};
